import { supabase } from '../lib/supabase';

// Get all merchandise with optional filters
export const getAllMerchandise = async (filters = {}) => {
  try {
    let query = supabase?.from('merchandise')?.select(`
        *,
        events (
          id,
          title,
          event_date
        )
      `)?.order('created_at', { ascending: false });

    if (filters?.search) {
      query = query?.or(`name.ilike.%${filters?.search}%,description.ilike.%${filters?.search}%,sku.ilike.%${filters?.search}%`);
    }

    if (filters?.event_id) {
      query = query?.eq('event_id', filters?.event_id);
    }

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    throw new Error(`Failed to fetch merchandise: ${error?.message}`);
  }
};

// Get merchandise by ID
export const getMerchandiseById = async (id) => {
  try {
    const { data, error } = await supabase?.from('merchandise')?.select(`
        *,
        events (
          id,
          title,
          event_date,
          organizer_id
        )
      `)?.eq('id', id)?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to fetch merchandise: ${error?.message}`);
  }
};

// Get merchandise by event ID
export const getMerchandiseByEventId = async (eventId) => {
  try {
    const { data, error } = await supabase?.from('merchandise')?.select('*')?.eq('event_id', eventId)?.eq('status', 'active')?.order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    throw new Error(`Failed to fetch event merchandise: ${error?.message}`);
  }
};

// Create new merchandise
export const createMerchandise = async (merchandiseData) => {
  try {
    const { data, error } = await supabase?.from('merchandise')?.insert([{
        event_id: merchandiseData?.event_id,
        name: merchandiseData?.name,
        description: merchandiseData?.description,
        price: merchandiseData?.price,
        sku: merchandiseData?.sku,
        stock_quantity: merchandiseData?.stock_quantity || 0,
        image_url: merchandiseData?.image_url,
        status: merchandiseData?.status || 'active'
      }])?.select(`
        *,
        events (
          id,
          title,
          event_date
        )
      `)?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to create merchandise: ${error?.message}`);
  }
};

// Update merchandise
export const updateMerchandise = async (id, merchandiseData) => {
  try {
    const { data, error } = await supabase?.from('merchandise')?.update({
        name: merchandiseData?.name,
        description: merchandiseData?.description,
        price: merchandiseData?.price,
        sku: merchandiseData?.sku,
        stock_quantity: merchandiseData?.stock_quantity,
        image_url: merchandiseData?.image_url,
        status: merchandiseData?.status
      })?.eq('id', id)?.select(`
        *,
        events (
          id,
          title,
          event_date
        )
      `)?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to update merchandise: ${error?.message}`);
  }
};

// Delete merchandise
export const deleteMerchandise = async (id) => {
  try {
    const { data, error } = await supabase?.from('merchandise')?.delete()?.eq('id', id)?.select()?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to delete merchandise: ${error?.message}`);
  }
};

// Update merchandise stock
export const updateMerchandiseStock = async (id, newStock) => {
  try {
    const { data, error } = await supabase?.from('merchandise')?.update({ 
        stock_quantity: newStock,
        status: newStock > 0 ? 'active' : 'out_of_stock'
      })?.eq('id', id)?.select()?.single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    throw new Error(`Failed to update merchandise stock: ${error?.message}`);
  }
};

// Get merchandise statistics
export const getMerchandiseStats = async (eventId = null) => {
  try {
    let query = supabase?.from('merchandise')?.select('id, price, stock_quantity, status, event_id');

    if (eventId) {
      query = query?.eq('event_id', eventId);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(error.message);
    }

    const stats = {
      total: data?.length || 0,
      totalValue: 0,
      totalStock: 0,
      byStatus: {
        active: 0,
        inactive: 0,
        out_of_stock: 0
      }
    };

    data?.forEach(item => {
      stats.totalValue += (item?.price || 0) * (item?.stock_quantity || 0);
      stats.totalStock += item?.stock_quantity || 0;
      stats.byStatus[item?.status] = (stats?.byStatus?.[item?.status] || 0) + 1;
    });

    return stats;
  } catch (error) {
    throw new Error(`Failed to get merchandise statistics: ${error?.message}`);
  }
};

export default {
  getAllMerchandise,
  getMerchandiseById,
  getMerchandiseByEventId,
  createMerchandise,
  updateMerchandise,
  deleteMerchandise,
  updateMerchandiseStock,
  getMerchandiseStats
};